import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { WHOAPI_URL } from '../../helpers/urlConst';

import { reportError } from '../utils/report-error';
import { initialErrorState } from '../utils/initial-error-state';

export const useEmailValidation = () => {
  const { t } = useTranslation();
  const flags = useFlags();

  const validateEmpty = email => {
    if (!email) {
      return {
        hasError: true,
        message: t('user_add.invalid_email_address'),
      };
    }
    return initialErrorState;
  };

  const validateFormat = email => {
    if (!email.includes('@')) {
      return {
        hasError: true,
        message: t('user_add.invalid_email_address'),
      };
    }
    return initialErrorState;
  };

  const validateWhoApi = async email => {
    const [, domain] = email.split('@');
    try {
      const result = await axios.get(
        `${WHOAPI_URL}?apikey=${process.env.REACT_APP_WHOAPI_KEY}&r=taken&domain=${domain}`,
      );
      // Reference: https://whoapi.com/api-documentation/
      // Section: API Status Codes
      if (result.data.status !== '0' || (result.data.status === '0' && result?.data?.taken === 0)) {
        return {
          hasError: true,
          message: t('user_add.invalid_email_address'),
        };
      }
      return initialErrorState;
    } catch (err) {
      reportError(err);
      return initialErrorState;
    }
  };

  const validators = [validateEmpty, validateFormat, validateWhoApi];

  const validateEmail = async (email, selectedValidators) => {
    const results = await Promise.all(
      selectedValidators.map(async validator => {
        const { hasError, message } = await validator(email);
        return { hasError, message };
      }),
    );
    const error = results.find(result => result.hasError);
    return error || initialErrorState;
  };

  const validate = async email => {
    if (flags['disable-email-validation']) {
      const filteredValidators = validators.filter(validator => validator !== validateWhoApi);
      return validateEmail(email, filteredValidators);
    }
    return validateEmail(email, validators);
  };

  return { validate };
};
